import { signOut, type Auth } from 'firebase/auth';
import { onSnapshotsInSync, waitForPendingWrites, type Firestore } from 'firebase/firestore';
import type { Connectivity } from './connectivity';
import { isFinalState, isUnsentState, type Ledger } from './ledger';
import { createStore, type ReadableStore } from './store';
import type { UploadQueue } from './uploads';

export interface SyncSnapshot {
  online: boolean;
  /** Epoch ms since which the device has been in its current online/offline state. */
  since: number;
  /** Commands the server has not finished with (pending, synced, not delivered). */
  pending: number;
  /** Commands the server has not received (pending, not delivered). */
  unsent: number;
  notDelivered: number;
  /** Rejected commands the person has not dismissed. */
  rejected: number;
  /** Photos and signatures on this phone not yet in Storage. */
  uploads: number;
  /** Epoch ms of the last time every listener was in sync with the server while online. */
  lastSyncedAt: number | null;
}

export interface SyncStatus extends ReadableStore<SyncSnapshot> {
  start(): void;
  dispose(): void;
}

export function summarizeLedger(entries: ReturnType<Ledger['getSnapshot']>): {
  pending: number;
  unsent: number;
  notDelivered: number;
  rejected: number;
} {
  let pending = 0;
  let unsent = 0;
  let notDelivered = 0;
  let rejected = 0;
  for (const e of entries) {
    if (!isFinalState(e.state)) pending += 1;
    if (isUnsentState(e.state)) unsent += 1;
    if (e.state === 'not_delivered') notDelivered += 1;
    if (e.state === 'rejected' && e.dismissed !== true) rejected += 1;
  }
  return { pending, unsent, notDelivered, rejected };
}

export function createSyncStatus(deps: {
  db: Firestore;
  ledger: Ledger;
  connectivity: Connectivity;
  /** Pending uploads are counted when given. */
  uploads?: Pick<UploadQueue, 'getSnapshot' | 'subscribe'>;
}): SyncStatus {
  const { db, ledger, connectivity, uploads } = deps;
  let lastSyncedAt: number | null = null;
  const build = (): SyncSnapshot => ({
    ...connectivity.getSnapshot(),
    ...summarizeLedger(ledger.getSnapshot()),
    uploads: uploads?.getSnapshot().pending ?? 0,
    lastSyncedAt,
  });
  const store = createStore<SyncSnapshot>(build());
  const refresh = (): void => {
    const next = build();
    const cur = store.getSnapshot();
    const same = (Object.keys(next) as (keyof SyncSnapshot)[]).every((k) => next[k] === cur[k]);
    if (!same) store.set(next);
  };
  const cleanups: (() => void)[] = [];
  return {
    subscribe: store.subscribe,
    getSnapshot: store.getSnapshot,
    start() {
      if (cleanups.length > 0) return;
      cleanups.push(ledger.subscribe(refresh), connectivity.subscribe(refresh));
      if (uploads) cleanups.push(uploads.subscribe(refresh));
      cleanups.push(
        onSnapshotsInSync(db, () => {
          if (connectivity.getSnapshot().online) {
            lastSyncedAt = Date.now();
            refresh();
          }
        }),
      );
      refresh();
    },
    dispose() {
      for (const c of cleanups.splice(0)) c();
    },
  };
}

/** `waitForPendingWrites` with a deadline. Returns false when the deadline passes first. */
export async function waitForPendingWritesWithin(
  db: Firestore,
  timeoutMs: number,
): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<false>((resolve) => {
    timer = setTimeout(() => resolve(false), timeoutMs);
  });
  try {
    return await Promise.race([waitForPendingWrites(db).then(() => true as const), timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

export type SignOutBlockReason = 'offline' | 'pending_writes' | 'error';

export type SignOutResult =
  { ok: true } | { ok: false; reason: SignOutBlockReason; unsent: number; message: string };

/**
 * Shared devices (02 §2.2): sign-out is refused while this person's commands have not reached the
 * server, so nobody inherits another person's queue. On success the local ledger is cleared.
 */
export async function signOutSafely(deps: {
  auth: Auth;
  db: Firestore;
  ledger: Ledger;
  connectivity: Connectivity;
  waitTimeoutMs?: number;
}): Promise<SignOutResult> {
  const { auth, db, ledger, connectivity, waitTimeoutMs = 10_000 } = deps;
  await ledger.ready;
  let unsent = summarizeLedger(ledger.getSnapshot()).unsent;

  if (unsent > 0) {
    if (!connectivity.getSnapshot().online) {
      return {
        ok: false,
        reason: 'offline',
        unsent,
        message: `${unsent} command${unsent === 1 ? '' : 's'} still waiting to sync. Go online first.`,
      };
    }
    await waitForPendingWritesWithin(db, waitTimeoutMs);
    // The command watchers move the ledger on the same snapshot that flushes the queue.
    await new Promise((resolve) => setTimeout(resolve, 0));
    unsent = summarizeLedger(ledger.getSnapshot()).unsent;
    if (unsent > 0) {
      return {
        ok: false,
        reason: 'pending_writes',
        unsent,
        message: `${unsent} command${unsent === 1 ? '' : 's'} could not be sent yet. Try again, or resend them first.`,
      };
    }
  }

  try {
    await signOut(auth);
    await ledger.clear();
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      reason: 'error',
      unsent: 0,
      message: err instanceof Error ? err.message : 'Sign out failed.',
    };
  }
}
